import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, Calendar, Eye, EyeOff } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';

const AdminNewsPage: React.FC = () => {
  const newsArticles = useAdminStore((s) => s.newsArticles);
  const deleteNewsArticle = useAdminStore((s) => s.deleteNewsArticle);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const published = (newsArticles ?? [])
    .filter((a) => a.isPublished)
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  const drafts = (newsArticles ?? []).filter((a) => !a.isPublished);

  const handleDelete = (): void => {
    if (!deleteId) return;
    deleteNewsArticle(deleteId);
    toast.success('Article deleted');
    setDeleteId(null);
  };

  const formatDate = (date: string): string =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const renderGroup = (title: string, items: typeof newsArticles): React.ReactNode => {
    if (items.length === 0) return null;
    return (
      <div>
        <h3 className="text-[10px] font-bold uppercase tracking-[0.3em] text-black/30 dark:text-white/30 mb-3">
          {title}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((article) => (
              <motion.div
                key={article._id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="group bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all"
              >
                <div className="flex">
                  <div className="w-32 h-32 shrink-0 bg-black/[0.02] dark:bg-white/[0.02] overflow-hidden">
                    {article.coverImage ? (
                      <img src={article.coverImage} alt={article.title.en} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-black/10 dark:text-white/10">
                        <Calendar size={24} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-4 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-[13px] font-bold truncate">{article.title.en}</h4>
                          {article.isPublished ? (
                            <Eye size={12} className="text-green-500 shrink-0" />
                          ) : (
                            <EyeOff size={12} className="text-black/20 dark:text-white/20 shrink-0" />
                          )}
                        </div>
                        <p className="text-[10px] text-black/40 dark:text-white/40 truncate">{article.excerpt.en}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Link
                          to={`/admin/news/${article._id}`}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all"
                        >
                          <Pencil size={12} />
                        </Link>
                        <button
                          onClick={() => setDeleteId(article._id)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-black/40 dark:text-white/40">
                      <Calendar size={10} />
                      <span>{formatDate(article.publishedAt)}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">News & Press</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {(newsArticles ?? []).length} article{(newsArticles ?? []).length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/admin/news/new"
          className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> New Article
        </Link>
      </div>

      {renderGroup('Published', published)}
      {renderGroup('Drafts', drafts)}

      {(newsArticles ?? []).length === 0 && (
        <div className="text-center py-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            No articles found
          </p>
          <p className="text-[10px] text-black/15 dark:text-white/15 mt-1">
            Create your first article to get started
          </p>
        </div>
      )}

      <AdminConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Article"
        message="This will permanently remove this article. This action cannot be undone."
      />
    </div>
  );
};

export default AdminNewsPage;
